import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, MessageCircle, Target, TrendingUp, Phone, Mail, Lightbulb, CheckCircle, AlertCircle, Loader2, Copy, Edit3, Send } from 'lucide-react';
import { generateSalesClosingStrategy, generateClosingScript, analyzeClosingOpportunity } from '../../../services/openai';

const AIClosingAssistant = ({ leads = [], onLeadUpdate }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [closingStrategy, setClosingStrategy] = useState(null);
  const [closingScript, setClosingScript] = useState(null);
  const [opportunityAnalysis, setOpportunityAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeAnalysis, setActiveAnalysis] = useState('strategy');
  const [scriptTechnique, setScriptTechnique] = useState('assumptive');
  const [conversation, setConversation] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (selectedLead) {
      loadLeadAnalysis();
    }
  }, [selectedLead]);

  const loadLeadAnalysis = async () => {
    if (!selectedLead) return;
    
    setLoading(true);
    try {
      // Generate initial analysis based on lead data
      const leadData = {
        name: selectedLead?.name,
        company: selectedLead?.company,
        email: selectedLead?.email,
        score: selectedLead?.score,
        status: selectedLead?.status,
        engagementLevel: selectedLead?.engagementLevel,
        potentialValue: selectedLead?.potentialValue,
        tags: selectedLead?.tags,
        lastContact: selectedLead?.lastContact
      };

      const salesContext = {
        timeline: '30 days',
        targetValue: selectedLead?.potentialValue,
        competition: 'moderate',
        urgency: selectedLead?.status === 'hot' ? 'high' : 'medium'
      };

      // Generate closing strategy
      const strategy = await generateSalesClosingStrategy(leadData, salesContext);
      setClosingStrategy(strategy);

      // Analyze opportunity
      const analysis = await analyzeClosingOpportunity(
        [{ contact: selectedLead?.lastContact, type: 'email', outcome: 'positive' }],
        { score: selectedLead?.score, engagement: selectedLead?.engagementLevel }
      );
      setOpportunityAnalysis(analysis);

    } catch (error) {
      console.error('Error loading lead analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateScript = async () => {
    if (!selectedLead) return;
    
    setLoading(true);
    try {
      const leadProfile = {
        name: selectedLead?.name,
        company: selectedLead?.company,
        role: 'Decision Maker',
        painPoints: ['efficiency', 'cost reduction', 'scalability'],
        interests: selectedLead?.tags,
        communicationStyle: 'professional',
        industry: 'technology'
      };

      const context = {
        meetingType: 'closing_call',
        previousInteractions: 3,
        timeframe: '30 minutes',
        urgency: selectedLead?.status === 'hot' ? 'high' : 'medium'
      };

      const script = await generateClosingScript(leadProfile, scriptTechnique, context);
      setClosingScript(script);
      setActiveAnalysis('script');
    } catch (error) {
      console.error('Error generating script:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
      // Could add a toast notification here
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!message?.trim() || !selectedLead) return;

    const userMessage = {
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setConversation(prev => [...prev, userMessage]);
    setMessage('');
    setLoading(true);

    try {
      // Simulate AI response - in real implementation, this would use OpenAI
      setTimeout(() => {
        const aiResponse = {
          type: 'assistant',
          content: `Based on your question about ${selectedLead?.name}, I recommend focusing on their ${selectedLead?.tags?.[0]} status and leveraging the fact that they're a ${selectedLead?.status} lead. Consider emphasizing the ROI and time-sensitive benefits.`,
          timestamp: new Date()
        };
        setConversation(prev => [...prev, aiResponse]);
        setLoading(false);
      }, 1500);
    } catch (error) {
      console.error('Error getting AI response:', error);
      setLoading(false);
    }
  };

  const getLeadStatusColor = (status) => {
    switch (status) {
      case 'hot': return 'bg-red-100 text-red-800';
      case 'warm': return 'bg-yellow-100 text-yellow-800';
      case 'cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEngagementColor = (level) => {
    switch (level) {
      case 'very-high': return 'text-green-600';
      case 'high': return 'text-green-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Lead Selection Panel */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Target className="h-5 w-5 mr-2 text-blue-600" />
              Select Lead
            </h3>
            <p className="text-sm text-gray-600 mt-1">Choose a lead to analyze and get AI-powered closing recommendations</p>
          </div>
          
          <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
            {leads?.map((lead) => (
              <motion.div
                key={lead?.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedLead(lead)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedLead?.id === lead?.id
                    ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{lead?.name}</h4>
                    <p className="text-sm text-gray-600">{lead?.company}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLeadStatusColor(lead?.status)}`}>
                    {lead?.status}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <TrendingUp className={`h-4 w-4 mr-1 ${getEngagementColor(lead?.engagementLevel)}`} />
                    <span className="text-gray-600">Score: {lead?.score}</span>
                  </div>
                  <span className="text-green-600 font-medium">
                    ${lead?.potentialValue?.toLocaleString()}
                  </span>
                </div>
                
                {lead?.tags && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {lead?.tags?.slice(0, 2)?.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                    {lead?.tags?.length > 2 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        +{lead?.tags?.length - 2} more
                      </span>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      {/* AI Analysis Panel */}
      <div className="lg:col-span-2">
        {!selectedLead ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Brain className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">AI Closing Assistant</h3>
            <p className="text-gray-600">Select a lead from the left panel to get AI-powered closing strategies, scripts, and recommendations.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Lead Info Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedLead?.name}</h3>
                  <p className="text-gray-600">{selectedLead?.company}</p>
                  <div className="flex items-center mt-2 space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLeadStatusColor(selectedLead?.status)}`}>
                      {selectedLead?.status} lead
                    </span>
                    <span className="text-sm text-gray-600">Score: {selectedLead?.score}</span>
                    <span className="text-sm text-green-600 font-medium">
                      ${selectedLead?.potentialValue?.toLocaleString()} potential
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Phone className="h-4 w-4 mr-1" />
                    Call
                  </button>
                  <button className="flex items-center px-3 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </button>
                </div>
              </div>
            </div>

            {/* Analysis Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  {[
                    { id: 'strategy', name: 'Closing Strategy', icon: Target },
                    { id: 'script', name: 'Script Generator', icon: MessageCircle },
                    { id: 'chat', name: 'AI Chat', icon: Brain }
                  ]?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveAnalysis(tab?.id)}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                        activeAnalysis === tab?.id
                          ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <tab.icon className="h-4 w-4 mr-2" />
                      {tab?.name}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeAnalysis === 'strategy' && (
                  <div className="space-y-6">
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">Generating AI strategy...</span>
                      </div>
                    ) : closingStrategy ? (
                      <div className="space-y-6">
                        {/* Lead Score & Probability */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-blue-600">Lead Readiness Score</span>
                              <span className="text-2xl font-bold text-blue-900">
                                {closingStrategy?.lead_score}
                              </span>
                            </div>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-green-600">Close Probability</span>
                              <span className="text-2xl font-bold text-green-900">
                                {closingStrategy?.closing_probability}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Recommended Techniques */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Lightbulb className="h-5 w-5 mr-2 text-yellow-500" />
                            Recommended Closing Techniques
                          </h4>
                          <div className="space-y-4">
                            {closingStrategy?.recommended_techniques?.map((technique, index) => (
                              <div key={index} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <h5 className="font-medium text-gray-900">{technique?.technique}</h5>
                                  <button
                                    onClick={() => handleCopyToClipboard(technique?.script_example)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{technique?.description}</p>
                                <div className="bg-white rounded p-3 border-l-4 border-blue-400">
                                  <p className="text-sm text-gray-800 italic">"{technique?.script_example}"</p>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                  <strong>When to use:</strong> {technique?.when_to_use}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Objection Handling */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2 text-orange-500" />
                            Objection Handling
                          </h4>
                          <div className="space-y-3">
                            {closingStrategy?.objection_handling?.map((objection, index) => (
                              <div key={index} className="bg-orange-50 rounded-lg p-4">
                                <h6 className="font-medium text-orange-900 mb-1">"{objection?.objection}"</h6>
                                <p className="text-sm text-orange-700 mb-2">{objection?.response_strategy}</p>
                                <div className="bg-white rounded p-3 border-l-4 border-orange-400">
                                  <p className="text-sm text-gray-800 italic">"{objection?.script}"</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Next Actions */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                            Recommended Next Actions
                          </h4>
                          <div className="space-y-2">
                            {closingStrategy?.next_actions?.map((action, index) => (
                              <div key={index} className="flex items-center p-3 bg-green-50 rounded-lg">
                                <div className={`w-3 h-3 rounded-full mr-3 ${
                                  action?.priority === 'high' ? 'bg-red-500' :
                                  action?.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                                }`}></div>
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900">{action?.action}</span>
                                  <span className="text-xs text-gray-500 ml-2">({action?.timeline})</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Brain className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600">Click "Generate Strategy" to get AI-powered closing recommendations</p>
                        <button
                          onClick={loadLeadAnalysis}
                          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Generate Strategy
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {activeAnalysis === 'script' && (
                  <div className="space-y-6">
                    {/* Script Configuration */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Closing Technique
                      </label>
                      <select
                        value={scriptTechnique}
                        onChange={(e) => setScriptTechnique(e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="assumptive">Assumptive Close</option>
                        <option value="urgency">Urgency Close</option>
                        <option value="summary">Summary Close</option>
                        <option value="question">Question Close</option>
                        <option value="alternative">Alternative Close</option>
                      </select>
                      <button
                        onClick={handleGenerateScript}
                        disabled={loading}
                        className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Edit3 className="h-4 w-4 mr-2" />
                        )}
                        Generate Script
                      </button>
                    </div>

                    {/* Generated Script */}
                    {closingScript && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">Opening</h4>
                          <p className="text-blue-800">{closingScript?.opening}</p>
                        </div>
                        
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-semibold text-green-900 mb-2">Main Script</h4>
                          <p className="text-green-800 whitespace-pre-line">{closingScript?.main_script}</p>
                        </div>
                        
                        <div className="bg-purple-50 rounded-lg p-4">
                          <h4 className="font-semibold text-purple-900 mb-2">Closing Question</h4>
                          <p className="text-purple-800 font-medium">{closingScript?.closing_question}</p>
                        </div>

                        {/* Key Points */}
                        <div className="bg-yellow-50 rounded-lg p-4">
                          <h4 className="font-semibold text-yellow-900 mb-2">Key Points to Emphasize</h4>
                          <ul className="space-y-1">
                            {closingScript?.key_points?.map((point, index) => (
                              <li key={index} className="flex items-start text-yellow-800">
                                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Alternative Approaches */}
                        {closingScript?.alternative_approaches?.length > 0 && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3">Alternative Approaches</h4>
                            <div className="space-y-3">
                              {closingScript?.alternative_approaches?.map((approach, index) => (
                                <div key={index} className="bg-white rounded-lg p-3 border">
                                  <h6 className="font-medium text-gray-800 mb-1">{approach?.scenario}</h6>
                                  <p className="text-sm text-gray-600">{approach?.script_variation}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeAnalysis === 'chat' && (
                  <div className="space-y-4">
                    {/* Conversation History */}
                    <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto space-y-3">
                      {conversation?.length === 0 ? (
                        <div className="text-center text-gray-500 mt-8">
                          <MessageCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>Start a conversation with your AI closing assistant</p>
                        </div>
                      ) : (
                        conversation?.map((msg, index) => (
                          <div key={index} className={`flex ${msg?.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              msg?.type === 'user' ?'bg-blue-600 text-white' :'bg-white text-gray-800 border'
                            }`}>
                              <p className="text-sm">{msg?.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                      {loading && (
                        <div className="flex justify-start">
                          <div className="bg-white text-gray-800 border px-4 py-2 rounded-lg">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e?.target?.value)}
                        onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
                        placeholder="Ask me anything about closing this lead..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!message?.trim() || loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIClosingAssistant;